import type { Application } from '@doxyme/extensions-graphql-types';
import { TrackedEvents } from '../../../features/tracking/track';
import { useTrack } from '../../useTrack';
import {
  UpgradeButtonLocation,
  useTrackUpgradeSubscriptionEvent,
} from '../../useTracking/useTrackUpgradeSubscriptionEvent';

export type AppPageCategory = 'library' | 'prototypes';
export type AppLibrarySortOption =
  | 'most_popular'
  | 'recently_added'
  | 'alphabetical';
export type AccessPrototypesModalLocation = 'footer_banner';

type UseExtensionsTrackingEventsReturnType = {
  trackAppsPageVisited: () => void;
  trackPrototypesPageVisited: () => void;
  trackAppCardClick: (app: Application) => void;
  trackAppInstallClick: (app: Application) => void;
  trackAppRemoveClick: (app: Application, location: string) => void;
  trackAppInstallSuccessful: (app: Application) => void;
  trackAppRemoveSuccessful: (app: Application, location: string) => void;
  trackAppInformationClick: (app: Application, informationType: string) => void;
  trackUpgradeButtonClick: (app: Application) => void;
  trackAppLibrarySortChanged: (sortBy: AppLibrarySortOption) => void;
  trackAccessPrototypesModalOpened: (
    location: AccessPrototypesModalLocation,
  ) => void;
  trackAccessPrototypesModalClosed: () => void;
  trackAccessPrototypesOptInCompleted: () => void;
};

const getAppData = (
  app: Application,
): {
  appId: Application['id'];
  appName: Application['manifest']['name'];
  appTitle: Application['manifest']['title'];
  appCategory: Application['category'];
} => ({
  appId: app.id,
  appName: app.manifest.name,
  appTitle: app.manifest.title,
  appCategory: app.category,
});

export const useExtensionsTrackingEvents =
  (): UseExtensionsTrackingEventsReturnType => {
    const track = useTrack();
    const { trackEvent } = useTrackUpgradeSubscriptionEvent();

    const trackAppsPageVisited = (): void => {
      void track(TrackedEvents.AppsPageVisited, {});
    };

    const trackPrototypesPageVisited = (): void => {
      void track(TrackedEvents.PrototypesPageVisited, {});
    };

    const trackAppCardClick = (app: Application): void => {
      void track(TrackedEvents.AppCardClicked, {
        ...getAppData(app),
        isInstalled: app.isInstalled,
      });
    };

    const trackAppInstallClick = (app: Application): void => {
      void track(TrackedEvents.AppInstallClicked, getAppData(app));
    };

    const trackAppRemoveClick = (app: Application, location: string): void => {
      void track(TrackedEvents.AppRemoveClicked, {
        ...getAppData(app),
        location,
      });
    };

    const trackAppInstallSuccessful = (app: Application): void => {
      void track(TrackedEvents.AppInstallSuccessful, getAppData(app));
    };

    const trackAppRemoveSuccessful = (
      app: Application,
      location: string,
    ): void => {
      void track(TrackedEvents.AppRemoveSuccessful, {
        ...getAppData(app),
        location,
      });
    };

    const trackAppInformationClick = (
      app: Application,
      informationType: string,
    ): void => {
      void track(TrackedEvents.AppInformationClicked, {
        ...getAppData(app),
        informationType,
      });
    };

    const trackUpgradeButtonClick = (app: Application): void => {
      trackEvent(TrackedEvents.UpgradeButtonClicked, {
        ...getAppData(app),
        location: UpgradeButtonLocation.AppsUpgradeButton,
      });
    };

    const trackAppLibrarySortChanged = (sortBy: AppLibrarySortOption): void => {
      void track(TrackedEvents.AppLibrarySortChanged, { sortBy });
    };

    const trackAccessPrototypesModalOpened = (
      location: AccessPrototypesModalLocation,
    ): void => {
      void track(TrackedEvents.AccessPrototypesModalOpened, { location });
    };

    const trackAccessPrototypesModalClosed = (): void => {
      void track(TrackedEvents.AccessPrototypesModalClosed, {});
    };

    const trackAccessPrototypesOptInCompleted = (): void => {
      void track(TrackedEvents.AccessPrototypesOptInCompleted, {});
    };

    return {
      trackAppsPageVisited,
      trackPrototypesPageVisited,
      trackAppCardClick,
      trackAppInstallClick,
      trackAppRemoveClick,
      trackAppInstallSuccessful,
      trackAppRemoveSuccessful,
      trackAppInformationClick,
      trackUpgradeButtonClick,
      trackAppLibrarySortChanged,
      trackAccessPrototypesModalOpened,
      trackAccessPrototypesModalClosed,
      trackAccessPrototypesOptInCompleted,
    };
  };
